import { useCallback } from 'react';
import { useToast } from './use-toast';
import { writeExcel, type WriteSheetData } from '@/lib/excelEngine';

/**
 * Hook for exporting empreendimentos data to Excel (XLSX) and PDF (print).
 * Uses excelEngine abstraction (ExcelJS) — no direct xlsx dependency.
 */
export function useEmpExport() {
  const { toast } = useToast();

  const exportToExcel = useCallback(async (
    data: Record<string, unknown>[],
    filename: string,
    sheetName = 'Dados'
  ) => {
    if (!data.length) {
      toast({ title: 'Sem dados para exportar', variant: 'destructive' });
      return;
    }

    try {
      await writeExcel([{ name: sheetName, data }], filename);
      toast({ title: 'Exportação concluída', description: `${filename}.xlsx baixado.` });
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Erro na exportação', description: String(err), variant: 'destructive' });
    }
  }, [toast]);

  const exportMultiSheet = useCallback(async (
    sheets: Array<{ name: string; data: Record<string, unknown>[] }>,
    filename: string
  ) => {
    const nonEmpty = sheets.filter(s => s.data.length > 0);
    if (!nonEmpty.length) {
      toast({ title: 'Sem dados para exportar', variant: 'destructive' });
      return;
    }

    try {
      await writeExcel(nonEmpty, filename);
      toast({ title: 'Exportação concluída', description: `${filename}.xlsx com ${nonEmpty.length} abas.` });
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Erro na exportação', description: String(err), variant: 'destructive' });
    }
  }, [toast]);

  const exportToPDF = useCallback((title: string) => {
    const originalTitle = document.title;
    document.title = title;
    window.print();
    document.title = originalTitle;
  }, []);

  return { exportToExcel, exportMultiSheet, exportToPDF };
}
